import { amplifyConfig } from "@/config/amplify";
import {
  ExtractedFieldData,
  MatchedShipmentData,
  SuggestedCorrection,
  ValidationMetadata,
} from "@/types/validation";

export interface Document {
  id: string;
  fileName: string;
  documentType?: string;
  status: "uploading" | "uploaded" | "processing" | "completed" | "error" | "review_required";
  entityType?: string;
  entityName?: string;
  uploadedAt: string;
  processingCompletedAt?: string;
  confidence?: number;
  imageUrl?: string;
  extractedData?: Record<string, ExtractedFieldData>;
  corrections?: Correction[];
  errorMessage?: string;
  validationMetadata?: ValidationMetadata;
  shipmentId?: number | null;
  proNumber?: string | null;
}

// Backend API correction response interface
interface ApiCorrection {
  id: number;
  extraction_id: number;
  field_name: string;
  original_value: string | null;
  corrected_value: string;
  correction_reason: string | null;
  corrected_by: string;
  corrected_at: string;
  notes: string | null;
}

// Backend API extraction response interface
interface ApiExtraction {
  id: number;
  document_id: number;
  extraction_model: string | null;
  overall_confidence: number | null;
  extracted_fields: Record<
    string,
    {
      value: string | number | boolean | null;
      confidence: number;
      bounding_box?: Record<string, unknown>;
      validation?: {
        status: string;
        reasoning?: string;
        suggested_action?: string;
        severity?: string;
        extracted_value?: string | null;
        existing_value?: string | null;
        original_value?: string | null;
        corrected_value?: string | null;
        data_sources?: string[];
        discrepancies?: Array<{
          source: string;
          value: string | number | null;
          reason?: string;
        }>;
        customer_patterns?: string;
      };
    }
  >;
  requires_review: boolean;
  extracted_at: string;
  corrections?: ApiCorrection[];
  // Top-level validation_metadata (extracted by Pydantic from _validation_metadata)
  validation_metadata?: Record<string, unknown> | null;
}

// Backend API response interface
interface ApiDocument {
  id: number;
  s3_bucket: string;
  s3_key: string;
  s3_version_id: string | null;
  document_type: string;
  document_category: string | null;
  source: string;
  shipment_id: number | null;
  pro_number: string | null;
  trip_number: number | null;
  route_id: number | null;
  route_stop_id: number | null;
  original_filename: string;
  content_type: string;
  file_size_bytes: number;
  page_count: number | null;
  status_id: number | null;
  processed_at: string | null;
  is_deleted: boolean;
  created_on: string;
  created_by: string;
  extractions?: ApiExtraction[];
}

/**
 * Map backend status_id to frontend status string
 */
function mapStatusIdToStatus(
  statusId: number | null
): "uploading" | "uploaded" | "processing" | "completed" | "error" | "review_required" {
  switch (statusId) {
    case 300: // PENDING
      return "uploaded";
    case 301: // UPLOADED
      return "uploaded";
    case 302: // PROCESSING
      return "processing";
    case 303: // EXTRACTED
      return "processing";
    case 304: // REQUIRES_REVIEW
      return "review_required";
    case 305: // CORRECTED
      return "review_required";
    case 306: // COMPLETE
      return "completed";
    case 307: // FAILED
      return "error";
    case 308: // DELETED
      return "error";
    default:
      return "uploaded";
  }
}

/**
 * Ensures a date string has the UTC 'Z' suffix for proper timezone conversion.
 * Returns undefined if the input is null or undefined.
 */
function ensureUtcSuffix(dateString: string | null | undefined): string | undefined {
  if (!dateString) return undefined;
  return dateString.endsWith("Z") ? dateString : `${dateString}Z`;
}

/**
 * Transform backend API document to frontend Document interface
 */
function transformApiDocument(apiDoc: ApiDocument): Document {
  // Get the latest extraction if available
  const latestExtraction =
    apiDoc.extractions && apiDoc.extractions.length > 0
      ? apiDoc.extractions[apiDoc.extractions.length - 1]
      : null;

  // Transform corrections from backend format
  const corrections: Correction[] | undefined = latestExtraction?.corrections
    ? latestExtraction.corrections.map((correction) => ({
        field: correction.field_name,
        originalValue: correction.original_value || "",
        correctedValue: correction.corrected_value,
        confidence: 100, // Corrections are always high confidence
        reason: correction.correction_reason,
        correctedAt: correction.corrected_at,
      }))
    : undefined;

  // Build a map of corrected fields for marking in extracted data
  const correctedFieldsMap = new Map<string, { original: string; corrected: string }>();
  corrections?.forEach((correction) => {
    correctedFieldsMap.set(correction.field, {
      original: correction.originalValue,
      corrected: correction.correctedValue,
    });
  });

  // Transform extracted fields to frontend format
  const extractedData: Record<string, ExtractedFieldData> | undefined = latestExtraction
    ? Object.entries(latestExtraction.extracted_fields).reduce(
        (acc, [key, value]) => {
          const correction = correctedFieldsMap.get(key);

          // Handle nested objects - preserve shipping entities (shipper, consignee, bill_to) as full objects
          let displayValue: unknown = value.value;
          const shippingEntityFields = ["shipper", "consignee", "bill_to"];

          if (
            typeof value.value === "object" &&
            value.value !== null &&
            !Array.isArray(value.value)
          ) {
            // For shipping entities, preserve the full object so we can render all details
            if (shippingEntityFields.includes(key)) {
              displayValue = value.value;
            } else {
              // For other complex objects (not arrays), use name if available, otherwise JSON string
              const objValue = value.value as Record<string, unknown>;
              displayValue = (objValue.name as string) || JSON.stringify(value.value);
            }
          }
          // Arrays are preserved as-is for formatFieldValue to handle

          const fieldData: ExtractedFieldData & { corrected?: boolean; originalValue?: string } = {
            // For shipping entities, always use the full object, never the correction string
            value:
              correction && !shippingEntityFields.includes(key)
                ? correction.corrected
                : (displayValue as
                    | string
                    | number
                    | boolean
                    | null
                    | unknown[]
                    | Record<string, unknown>),
            confidence: typeof value.confidence === "number" ? value.confidence : 0,
            bounding_box: value.bounding_box as
              | { x: number; y: number; width: number; height: number; page?: number }
              | undefined,
            validation: value.validation
              ? {
                  status: value.validation.status as
                    | "suggested_add"
                    | "suggested_update"
                    | "suggested_review"
                    | "auto_corrected"
                    | "flagged"
                    | "requires_review"
                    | "validated",
                  reasoning: value.validation.reasoning,
                  suggested_action: value.validation.suggested_action as
                    | "add"
                    | "update"
                    | "review"
                    | undefined,
                  severity: value.validation.severity as "minor" | "moderate" | "major" | undefined,
                  extracted_value: value.validation.extracted_value,
                  existing_value: value.validation.existing_value,
                  original_value: value.validation.original_value,
                  corrected_value: value.validation.corrected_value,
                  data_sources: (value.validation.data_sources || []) as Array<
                    | "ocr"
                    | "vision"
                    | "shipment_db"
                    | "customer_history"
                    | "rate_table"
                    | "entity_lookup"
                  >,
                  discrepancies: (value.validation.discrepancies || []).map((d) => ({
                    source: d.source as
                      | "ocr"
                      | "vision"
                      | "shipment_db"
                      | "customer_history"
                      | "rate_table"
                      | "entity_lookup",
                    value: d.value,
                    reason: d.reason,
                  })),
                  customer_patterns: value.validation.customer_patterns,
                }
              : undefined,
            corrected: !!correction,
            originalValue: correction?.original,
          };
          acc[key] = fieldData;
          return acc;
        },
        {} as Record<string, ExtractedFieldData>
      )
    : undefined;

  // Extract validation metadata - check both top-level and nested locations
  let validationMetadata: ValidationMetadata | undefined;

  // First, try to get from top-level validation_metadata (preferred - from Pydantic transformation)
  if (latestExtraction?.validation_metadata) {
    const rawMetadata = latestExtraction.validation_metadata as Record<string, unknown>;
    validationMetadata = {
      summary: (rawMetadata.summary as string) || null,
      recommendation: (rawMetadata.recommendation as string) || null,
      requires_manual_review: (rawMetadata.requires_manual_review as boolean) || false,
      validation_checks: (rawMetadata.validation_checks as Record<string, boolean>) || {},
      suggested_corrections: (rawMetadata.suggested_corrections as SuggestedCorrection[]) || [],
      metadata: (rawMetadata.metadata as Record<string, unknown>) || {},
      matched_shipment_data: (rawMetadata.matched_shipment_data as MatchedShipmentData) || null,
    };
  }
  // Fallback: check inside extracted_fields["_validation_metadata"] for backward compatibility
  else if (
    latestExtraction?.extracted_fields &&
    latestExtraction.extracted_fields["_validation_metadata"]
  ) {
    const rawMetadata = latestExtraction.extracted_fields["_validation_metadata"].value as Record<
      string,
      unknown
    > | null;
    if (rawMetadata) {
      validationMetadata = {
        summary: (rawMetadata.summary as string) || null,
        recommendation: (rawMetadata.recommendation as string) || null,
        requires_manual_review: (rawMetadata.requires_manual_review as boolean) || false,
        validation_checks: (rawMetadata.validation_checks as Record<string, boolean>) || {},
        suggested_corrections: (rawMetadata.suggested_corrections as SuggestedCorrection[]) || [],
        metadata: (rawMetadata.metadata as Record<string, unknown>) || {},
        matched_shipment_data: (rawMetadata.matched_shipment_data as MatchedShipmentData) || null,
      };
    }
  }

  // Remove _validation_metadata from extractedData if present
  if (extractedData && extractedData["_validation_metadata"]) {
    delete extractedData["_validation_metadata"];
  }

  return {
    id: apiDoc.id.toString(),
    fileName: apiDoc.original_filename,
    documentType: apiDoc.document_type,
    status: mapStatusIdToStatus(apiDoc.status_id),
    entityType: apiDoc.shipment_id ? "shipment" : apiDoc.trip_number ? "trip" : undefined,
    entityName: apiDoc.shipment_id
      ? `Shipment: ${apiDoc.shipment_id}`
      : apiDoc.trip_number
        ? `Trip: ${apiDoc.trip_number}`
        : undefined,
    // Ensure UTC dates from API have Z suffix for proper timezone conversion
    uploadedAt: ensureUtcSuffix(apiDoc.created_on) ?? new Date().toISOString(),
    processingCompletedAt: ensureUtcSuffix(apiDoc.processed_at),
    confidence: latestExtraction?.overall_confidence
      ? latestExtraction.overall_confidence * 100
      : undefined,
    extractedData,
    corrections,
    validationMetadata,
    shipmentId: apiDoc.shipment_id,
    proNumber: apiDoc.pro_number,
  };
}

export interface Correction {
  field: string;
  originalValue: string;
  correctedValue: string;
  confidence: number;
  reason?: string | null;
  correctedAt?: string;
}

export interface UploadResponse {
  document_id: number;
  presigned_url: string;
  expires_in: number;
  s3_key: string;
  s3_bucket: string;
}

export interface ConfirmUploadRequest {
  fileName: string;
  fileSize: number;
  mimeType: string;
}

export interface DocumentFilters {
  documentType?: string;
  documentTypes?: string[]; // Multiple types for filtering
  shipmentId?: number;
  shipmentIds?: number[]; // Multiple shipment IDs
  proNumber?: string;
  proNumberLike?: string; // Partial match with ILIKE
  proNumbers?: string[]; // Multiple exact PRO numbers
  tripNumber?: number;
  routeId?: number;
  statusId?: number;
  statusIds?: number[]; // Multiple statuses for filtering
  startDate?: string; // ISO date string for date range
  endDate?: string; // ISO date string for date range
  filename?: string; // For searching by filename
  page?: number; // Page number (1-indexed)
  pageSize?: number; // Items per page (default: 25, max: 100)
  sortBy?: string; // Field to sort by
  sortDir?: "asc" | "desc"; // Sort direction
}

/**
 * Response model for aggregated document metrics
 */
export interface MetricsData {
  total_completed: number;
  in_process: number;
  requires_review: number;
}

/**
 * Response model for available document types
 */
export interface DocumentTypesResponse {
  types: string[];
}

/**
 * Response model for paginated document list
 */
export interface PaginatedDocumentResponse {
  documents: Document[];
  total: number;
  page: number;
  pageSize: number;
  totalPages: number;
}

/**
 * Get list of all documents with optional filters
 */
export async function listDocuments(
  accessToken: string,
  filters?: DocumentFilters
): Promise<PaginatedDocumentResponse> {
  try {
    // Build query parameters
    const params = new URLSearchParams();

    if (filters?.documentType) {
      params.append("document_type", filters.documentType);
    }
    // Support multiple document types
    if (filters?.documentTypes && filters.documentTypes.length > 0) {
      params.append("document_types", filters.documentTypes.join(","));
    }
    if (filters?.shipmentId !== undefined) {
      params.append("shipment_id", filters.shipmentId.toString());
    }
    // Support multiple shipment IDs
    if (filters?.shipmentIds && filters.shipmentIds.length > 0) {
      params.append("shipment_ids", filters.shipmentIds.join(","));
    }
    if (filters?.proNumber) {
      params.append("pro_number", filters.proNumber);
    }
    // Support partial PRO number matching
    if (filters?.proNumberLike) {
      params.append("pro_number_like", filters.proNumberLike);
    }
    // Support multiple exact PRO numbers
    if (filters?.proNumbers && filters.proNumbers.length > 0) {
      params.append("pro_numbers", filters.proNumbers.join(","));
    }
    if (filters?.tripNumber !== undefined) {
      params.append("trip_number", filters.tripNumber.toString());
    }
    if (filters?.routeId !== undefined) {
      params.append("route_id", filters.routeId.toString());
    }
    if (filters?.statusId !== undefined) {
      params.append("status_id", filters.statusId.toString());
    }
    // Support multiple status IDs
    if (filters?.statusIds && filters.statusIds.length > 0) {
      params.append("status_ids", filters.statusIds.join(","));
    }
    // Support date range filtering
    if (filters?.startDate) {
      params.append("start_date", filters.startDate);
    }
    if (filters?.endDate) {
      params.append("end_date", filters.endDate);
    }
    // Support pagination parameters
    if (filters?.page !== undefined) {
      params.append("page", filters.page.toString());
    }
    if (filters?.pageSize !== undefined) {
      params.append("page_size", filters.pageSize.toString());
    }
    // Support sorting parameters
    if (filters?.sortBy) {
      params.append("sort_by", filters.sortBy);
    }
    if (filters?.sortDir) {
      params.append("sort_dir", filters.sortDir);
    }

    const queryString = params.toString();
    const url = `${amplifyConfig.apiUrl}/v1${queryString ? `?${queryString}` : ""}`;

    const response = await fetch(url, {
      method: "GET",
      headers: {
        Authorization: `Bearer ${accessToken}`,
        "Content-Type": "application/json",
      },
    });

    if (!response.ok) {
      throw new Error(`Failed to fetch documents: ${response.status}`);
    }

    const data = await response.json();
    const apiDocuments: ApiDocument[] = data.documents || [];
    let documents = apiDocuments.map(transformApiDocument);

    // Client-side filename filter (since backend doesn't support it)
    if (filters?.filename) {
      const searchTerm = filters.filename.toLowerCase();
      documents = documents.filter((doc) => doc.fileName.toLowerCase().includes(searchTerm));
    }

    return {
      documents,
      total: data.total ?? documents.length,
      page: data.page ?? 1,
      pageSize: data.page_size ?? 25,
      totalPages: data.total_pages ?? 1,
    };
  } catch (error) {
    // eslint-disable-next-line no-console
    console.error("Error fetching documents:", error);
    throw error;
  }
}

/**
 * Get all document IDs matching filters (for reset all functionality)
 * Fetches all pages to get complete list of IDs
 */
export async function getAllDocumentIds(
  accessToken: string,
  filters?: Omit<DocumentFilters, "page" | "pageSize">
): Promise<number[]> {
  try {
    const allIds: number[] = [];
    let currentPage = 1;
    let totalPages = 1;
    const pageSize = 100; // Max page size for efficiency

    do {
      const response = await listDocuments(accessToken, {
        ...filters,
        page: currentPage,
        pageSize,
      });

      // Extract document IDs
      response.documents.forEach((doc) => {
        allIds.push(parseInt(doc.id, 10));
      });

      totalPages = response.totalPages;
      currentPage++;
    } while (currentPage <= totalPages);

    return allIds;
  } catch (error) {
    // eslint-disable-next-line no-console
    console.error("Error fetching all document IDs:", error);
    throw error;
  }
}

/**
 * Get a specific document by ID
 */
export async function getDocumentById(documentId: string, accessToken: string): Promise<Document> {
  try {
    const response = await fetch(`${amplifyConfig.apiUrl}/v1/${documentId}`, {
      method: "GET",
      headers: {
        Authorization: `Bearer ${accessToken}`,
        "Content-Type": "application/json",
      },
    });

    if (!response.ok) {
      throw new Error(`Failed to fetch document: ${response.status}`);
    }

    const apiDoc: ApiDocument = await response.json();
    return transformApiDocument(apiDoc);
  } catch (error) {
    // eslint-disable-next-line no-console
    console.error("Error fetching document:", error);
    throw error;
  }
}

export interface UploadInitiateRequest {
  document_type: string;
  document_category?: string;
  source: string;
  source_type?: string;
  original_filename: string;
  content_type: string;
  file_size_bytes: number;
  shipment_id?: number;
  pro_number?: string;
  trip_number?: number;
  route_id?: number;
  route_stop_id?: number;
}

/**
 * Initiate document upload and get presigned URL
 */
export async function uploadDocument(
  file: File,
  accessToken: string,
  documentType: string = "UNKNOWN"
): Promise<UploadResponse> {
  try {
    // Prepare request body with file metadata
    const requestBody: UploadInitiateRequest = {
      document_type: documentType,
      source: "idoc_admin",
      source_type: "idoc_admin",
      original_filename: file.name,
      content_type: file.type,
      file_size_bytes: file.size,
      // Don't send shipment_id - let the agent determine it from extracted data
    };

    const response = await fetch(`${amplifyConfig.apiUrl}/v1/upload`, {
      method: "POST",
      headers: {
        Authorization: `Bearer ${accessToken}`,
        "Content-Type": "application/json",
      },
      body: JSON.stringify(requestBody),
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => null);
      throw new Error(errorData?.detail || `Failed to initiate upload: ${response.status}`);
    }

    const data = await response.json();
    return data;
  } catch (error) {
    // eslint-disable-next-line no-console
    console.error("Error initiating upload:", error);
    throw error;
  }
}

/**
 * Upload file to S3 using presigned URL
 */
export async function uploadFileToS3(
  file: File,
  uploadUrl: string,
  fields?: Record<string, string>,
  onProgress?: (progress: number) => void
): Promise<void> {
  try {
    const formData = new FormData();

    // Add fields if provided (for POST uploads)
    if (fields) {
      Object.entries(fields).forEach(([key, value]) => {
        formData.append(key, value);
      });
    }

    // For presigned PUT URLs, send the file directly (not as FormData)
    return new Promise((resolve, reject) => {
      const xhr = new XMLHttpRequest();

      // Track upload progress
      if (onProgress) {
        xhr.upload.addEventListener("progress", (event) => {
          if (event.lengthComputable) {
            const progress = (event.loaded / event.total) * 100;
            onProgress(progress);
          }
        });
      }

      xhr.addEventListener("load", () => {
        if (xhr.status >= 200 && xhr.status < 300) {
          resolve();
        } else {
          reject(new Error(`S3 upload failed: ${xhr.status}`));
        }
      });

      xhr.addEventListener("error", () => {
        reject(new Error("S3 upload failed"));
      });

      // Use PUT for presigned URL upload, send file directly
      xhr.open("PUT", uploadUrl);
      xhr.setRequestHeader("Content-Type", file.type);
      xhr.send(file);
    });
  } catch (error) {
    // eslint-disable-next-line no-console
    console.error("Error uploading file to S3:", error);
    throw error;
  }
}

/**
 * Confirm upload completion and trigger processing
 */
export async function confirmUpload(
  documentId: string,
  request: ConfirmUploadRequest,
  accessToken: string
): Promise<Document> {
  try {
    const response = await fetch(`${amplifyConfig.apiUrl}/v1/upload/confirm/${documentId}`, {
      method: "POST",
      headers: {
        Authorization: `Bearer ${accessToken}`,
        "Content-Type": "application/json",
      },
      body: JSON.stringify(request),
    });

    if (!response.ok) {
      throw new Error(`Failed to confirm upload: ${response.status}`);
    }

    const data = await response.json();
    return data;
  } catch (error) {
    // eslint-disable-next-line no-console
    console.error("Error confirming upload:", error);
    throw error;
  }
}

export interface DocumentDownloadResponse {
  document_id: number;
  download_url: string;
  expires_in: number;
  filename: string;
  content_type: string;
}

/**
 * Get presigned URL for viewing/downloading a document
 */
export async function getDocumentViewUrl(
  documentId: string,
  accessToken: string
): Promise<DocumentDownloadResponse> {
  try {
    const response = await fetch(`${amplifyConfig.apiUrl}/v1/${documentId}/download`, {
      method: "GET",
      headers: {
        Authorization: `Bearer ${accessToken}`,
        "Content-Type": "application/json",
      },
    });

    if (!response.ok) {
      throw new Error(`Failed to get document URL: ${response.status}`);
    }

    return await response.json();
  } catch (error) {
    // eslint-disable-next-line no-console
    console.error("Error getting document URL:", error);
    throw error;
  }
}

/**
 * Download document file
 */
export async function downloadDocument(documentId: string, accessToken: string): Promise<Blob> {
  try {
    // First get the presigned URL
    const urlResponse = await getDocumentViewUrl(documentId, accessToken);

    // Then fetch the actual file from the presigned URL
    const fileResponse = await fetch(urlResponse.download_url);

    if (!fileResponse.ok) {
      throw new Error(`Failed to download document file: ${fileResponse.status}`);
    }

    return await fileResponse.blob();
  } catch (error) {
    // eslint-disable-next-line no-console
    console.error("Error downloading document:", error);
    throw error;
  }
}

/**
 * Complete upload flow: initiate, upload to S3, confirm
 */
export async function completeUpload(
  file: File,
  accessToken: string,
  documentType: string = "UNKNOWN",
  onProgress?: (progress: number) => void
): Promise<Document> {
  try {
    // Step 1: Initiate upload and get presigned URL
    onProgress?.(10);
    const { document_id, presigned_url } = await uploadDocument(file, accessToken, documentType);

    // Step 2: Upload file to S3 using presigned URL
    onProgress?.(30);
    await uploadFileToS3(file, presigned_url, undefined, (progress) => {
      // Map S3 upload progress (0-100) to overall progress (30-90)
      const mappedProgress = 30 + (progress * 60) / 100;
      onProgress?.(mappedProgress);
    });

    // Step 3: Confirm upload completion
    onProgress?.(95);
    const confirmRequest: ConfirmUploadRequest = {
      fileName: file.name,
      fileSize: file.size,
      mimeType: file.type,
    };
    const document = await confirmUpload(String(document_id), confirmRequest, accessToken);

    onProgress?.(100);
    return document;
  } catch (error) {
    // eslint-disable-next-line no-console
    console.error("Error completing upload:", error);
    throw error;
  }
}

/**
 * Request model for bulk delete operation
 */
export interface BulkDeleteRequest {
  document_ids: number[];
  hard_delete: boolean;
}

/**
 * Response model for bulk delete operation
 */
export interface BulkDeleteResponse {
  deleted_count: number;
  failed_count: number;
  errors: string[];
}

/**
 * Delete a document
 * @param documentId - The document ID to delete
 * @param accessToken - Auth token
 * @param hardDelete - If true, permanently deletes document and S3 file. If false, soft-deletes (default)
 */
export async function deleteDocument(
  documentId: string,
  accessToken: string,
  hardDelete: boolean = false
): Promise<void> {
  try {
    const params = new URLSearchParams();
    if (hardDelete) {
      params.append("hard_delete", "true");
    }

    const queryString = params.toString();
    const url = `${amplifyConfig.apiUrl}/v1/${documentId}${queryString ? `?${queryString}` : ""}`;

    const response = await fetch(url, {
      method: "DELETE",
      headers: {
        Authorization: `Bearer ${accessToken}`,
        "Content-Type": "application/json",
      },
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => null);
      throw new Error(errorData?.detail || `Failed to delete document: ${response.status}`);
    }
  } catch (error) {
    // eslint-disable-next-line no-console
    console.error("Error deleting document:", error);
    throw error;
  }
}

/**
 * Bulk delete multiple documents
 * @param documentIds - Array of document IDs to delete
 * @param accessToken - Auth token
 * @param hardDelete - If true, permanently deletes documents and S3 files. If false, soft-deletes (default)
 */
export async function bulkDeleteDocuments(
  documentIds: number[],
  accessToken: string,
  hardDelete: boolean = false
): Promise<BulkDeleteResponse> {
  try {
    const requestBody: BulkDeleteRequest = {
      document_ids: documentIds,
      hard_delete: hardDelete,
    };

    const response = await fetch(`${amplifyConfig.apiUrl}/v1/bulk-delete`, {
      method: "POST",
      headers: {
        Authorization: `Bearer ${accessToken}`,
        "Content-Type": "application/json",
      },
      body: JSON.stringify(requestBody),
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => null);
      throw new Error(errorData?.detail || `Failed to bulk delete documents: ${response.status}`);
    }

    const data: BulkDeleteResponse = await response.json();
    return data;
  } catch (error) {
    // eslint-disable-next-line no-console
    console.error("Error bulk deleting documents:", error);
    throw error;
  }
}

/**
 * Filter options for metrics endpoint
 */
export interface MetricsFilters {
  startDate?: string; // ISO date string
  endDate?: string; // ISO date string
}

/**
 * Fetch aggregated document metrics
 * Returns counts of documents grouped by status category
 *
 * @param accessToken - Auth token
 * @param filters - Optional date filters for completed/review counts (in_process is always unfiltered)
 */
export async function fetchMetrics(
  accessToken: string,
  filters?: MetricsFilters
): Promise<MetricsData> {
  try {
    const params = new URLSearchParams();

    if (filters?.startDate) {
      params.append("start_date", filters.startDate);
    }
    if (filters?.endDate) {
      params.append("end_date", filters.endDate);
    }

    const queryString = params.toString();
    const url = `${amplifyConfig.apiUrl}/v1/metrics${queryString ? `?${queryString}` : ""}`;

    const response = await fetch(url, {
      method: "GET",
      headers: {
        Authorization: `Bearer ${accessToken}`,
        "Content-Type": "application/json",
      },
    });

    if (!response.ok) {
      throw new Error(`Failed to fetch metrics: ${response.status}`);
    }

    const data: MetricsData = await response.json();
    return data;
  } catch (error) {
    // eslint-disable-next-line no-console
    console.error("Error fetching metrics:", error);
    throw error;
  }
}

/**
 * Fetch available document types
 * Returns distinct document types from the database for filter dropdowns
 */
export async function fetchDocumentTypes(accessToken: string): Promise<string[]> {
  try {
    const response = await fetch(`${amplifyConfig.apiUrl}/v1/types`, {
      method: "GET",
      headers: {
        Authorization: `Bearer ${accessToken}`,
        "Content-Type": "application/json",
      },
    });

    if (!response.ok) {
      throw new Error(`Failed to fetch document types: ${response.status}`);
    }

    const data: DocumentTypesResponse = await response.json();
    return data.types;
  } catch (error) {
    // eslint-disable-next-line no-console
    console.error("Error fetching document types:", error);
    throw error;
  }
}
